import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import { FormControlLabel, Switch } from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    width: "100%",
  },
}));

const ServiceSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto").required("Obrigatório"),
  durationMinutes: Yup.number()
    .min(5, "Mínimo de 5 minutos")
    .required("Obrigatório"),
  price: Yup.number().min(0, "Inválido"),
});

const ServiceModal = ({ open, onClose, serviceId, reload }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    durationMinutes: 30,
    price: 0,
    active: true,
  };

  const [service, setService] = useState(initialState);
  const [active, setActive] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        const list = Array.isArray(data) ? data : data.users || [];
        // só faz sentido vincular profissionais habilitados
        setUsers(list.filter((u) => u.isServiceProvider));
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, [open]);

  useEffect(() => {
    const fetchService = async () => {
      if (!serviceId) {
        setService(initialState);
        setActive(true);
        setSelectedUserIds([]);
        return;
      }
      try {
        const { data } = await api.get(`/services/${serviceId}`);
        setService((prev) => ({ ...prev, ...data }));
        setActive(data.active);
        setSelectedUserIds((data.users || []).map((u) => u.id));
      } catch (err) {
        toastError(err);
      }
    };
    fetchService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, open]);

  const handleClose = () => {
    setService(initialState);
    setActive(true);
    setSelectedUserIds([]);
    onClose();
  };

  const handleSaveService = async (values) => {
    const serviceData = {
      ...values,
      active,
      userIds: selectedUserIds,
    };
    try {
      if (serviceId) {
        await api.put(`/services/${serviceId}`, serviceData);
      } else {
        await api.post("/services", serviceData);
      }
      toast.success("Serviço salvo com sucesso.");
      if (typeof reload === "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
        <DialogTitle id="form-dialog-title">
          {serviceId ? "Editar Serviço" : "Novo Serviço"}
        </DialogTitle>
        <Formik
          initialValues={service}
          enableReinitialize={true}
          validationSchema={ServiceSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveService(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label="Nome do serviço"
                    name="name"
                    autoFocus
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label="Duração (minutos)"
                    name="durationMinutes"
                    type="number"
                    error={touched.durationMinutes && Boolean(errors.durationMinutes)}
                    helperText={touched.durationMinutes && errors.durationMinutes}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label="Preço (R$)"
                    name="price"
                    type="number"
                    error={touched.price && Boolean(errors.price)}
                    helperText={touched.price && errors.price}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <FormControl variant="outlined" margin="dense" className={classes.formControl}>
                  <InputLabel id="providers-label">Profissionais</InputLabel>
                  <Select
                    multiple
                    labelId="providers-label"
                    value={selectedUserIds}
                    onChange={(e) => setSelectedUserIds(e.target.value)}
                    label="Profissionais"
                    renderValue={(selected) =>
                      users
                        .filter((u) => selected.includes(u.id))
                        .map((u) => u.name)
                        .join(", ")
                    }
                  >
                    {users.length === 0 && (
                      <MenuItem disabled value="">
                        Nenhum profissional habilitado
                      </MenuItem>
                    )}
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        <Checkbox checked={selectedUserIds.indexOf(u.id) > -1} />
                        <ListItemText primary={u.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Ativo"
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {serviceId ? "Salvar" : "Adicionar"}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ServiceModal;
